/**
 * Tabsync (Tomáš Pohl, measured on prod D1, 2026-08-19): a tab that lost its
 * identity says so and follows it - immediately, never with a generic error.
 *
 * The impersonation swaps the ONE `__Host-launchpad_session` cookie of the
 * whole browser, but each tab keeps rendering the identity it MOUNTED with.
 * Measured incident: Tomáš ended an impersonation in one tab, his OTHER tab
 * kept the client view over the now-staff cookie, every click answered
 * 401/403/404 as the generic sentence ("spadlo to"), and twelve minutes later
 * the stale bar's clock fired an end POST that answered 403 - and the tab
 * "walked back to the console" over an end that never ran.
 *
 * This module owns the ONE comparison and the ONE reaction. The reaction is a
 * full `window.location.reload()`: the two shells (client Dashboard vs
 * StaffConsole) are different worlds, and the surface a reload lands on
 * (console, client view, login) already explains itself. Never a partial
 * state patch.
 *
 * Three triggers, cheapest first:
 *  1. BroadcastChannel `launchpad-identity` - posted by every act that swaps
 *     the cookie (impersonation start and end, mode switch, logout, client
 *     switch). Same origin, same browser: exactly the cookie's scope. The
 *     message is a HINT; /api/auth/me is the truth, so the receiver re-checks
 *     before reloading.
 *  2. visibilitychange + window focus - a stale tab is almost always a
 *     BACKGROUND tab; the moment it is looked at again it re-checks. One
 *     listener pair owned here, installed once by App: the existing focus
 *     refreshes are per shell (Dashboard's loadHome, the console's own), and
 *     identity is app-wide, so riding them would mean several seams instead
 *     of one.
 *  3. Any 401/403 from our own /api - the fetch observer re-checks /me ONCE
 *     before the response reaches the surface. Identity changed: reload,
 *     never the generic sentence. Identity unchanged: the response passes and
 *     a real refusal stays a refusal.
 */
import type { AuthMeResponse } from "../shared/dto";

/** What one identity IS, read from /api/auth/me. */
export interface IdentityFingerprint {
  /** Optional: an older Worker payload may not carry it yet. */
  sessionId: string | null;
  userId: string;
  staffRole: "partner" | "admin" | null;
  activeClientId: string | null;
  impersonation: { clientId: string; mode: "look" | "act" } | null;
}

/**
 * `"guest"` = the tab mounted the login screen. `null` = the tab does not
 * know its own identity yet (loading, error) - the watcher stays quiet
 * rather than reloading over an identity it cannot compare.
 */
export type MountedIdentity = IdentityFingerprint | "guest" | null;

export function identityFingerprint(data: AuthMeResponse): IdentityFingerprint {
  return {
    sessionId: data.sessionId ?? null,
    userId: data.user.id,
    staffRole: data.user.staffRole,
    activeClientId: data.activeClientId,
    impersonation: data.impersonation
      ? { clientId: data.impersonation.clientId, mode: data.impersonation.mode }
      : null
  };
}

/**
 * 🔴 The session id is compared ONLY while impersonating. `loadSession`
 * (worker/src/security.ts) silently rotates a NON-impersonated session in its
 * second half of life (sliding renewal, at most daily) - the id changes, the
 * identity does not, and comparing it would reload background tabs over
 * nothing. Impersonated sessions are excluded from that renewal by name
 * (`!session.impersonatedBy`), so a different id there is always a REAL swap:
 * it is what tells a stale hour of client X apart from a fresh hour of the
 * same client X, whose CSRF and clock are different facts.
 */
export function identitiesMatch(
  a: IdentityFingerprint | "guest",
  b: IdentityFingerprint | "guest"
): boolean {
  if (a === "guest" || b === "guest") return a === b;
  if (a.userId !== b.userId) return false;
  if (a.staffRole !== b.staffRole) return false;
  if (a.activeClientId !== b.activeClientId) return false;
  if ((a.impersonation === null) !== (b.impersonation === null)) return false;
  if (a.impersonation && b.impersonation) {
    if (a.impersonation.clientId !== b.impersonation.clientId) return false;
    if (a.impersonation.mode !== b.impersonation.mode) return false;
    // Additive-optional field: only compare when both sides carry it.
    if (a.sessionId && b.sessionId && a.sessionId !== b.sessionId) return false;
  }
  return true;
}

const CHANNEL_NAME = "launchpad-identity";

let channel: BroadcastChannel | null = null;
let baseFetch: typeof fetch | null = null;
let getMounted: (() => MountedIdentity) | null = null;
let inflight: Promise<"match" | "changed" | "unknown"> | null = null;
let lastPassiveCheckAt = 0;
let onStaleCsrf: (() => void) | null = null;

/**
 * 🔴 The page holds ONE CSRF token, taken from `/api/auth/me` when the app
 * mounted, and the server renews a session older than a day underneath it. The
 * server keeps the replaced token acceptable for two days (worker/src/
 * security.ts, CSRF_GRACE_SECONDS), so this is the LAST line: a tab open past
 * that window learns its token is stale from the refusal itself and takes a
 * fresh one, instead of answering every write with a sentence the reader
 * cannot act on. Registered by App; the refusal still reaches the surface, so
 * nothing is hidden, and the next attempt carries the new token.
 */
export function setStaleCsrfHandler(handler: (() => void) | null): void {
  onStaleCsrf = handler;
}

/**
 * F161: the fetch observer below sees every fetch, but the voice recorder
 * uploads its take through XMLHttpRequest (for upload progress), which the
 * observer never sees. A refusal that arrives that way reports itself here, so
 * the repair is the same whichever transport carried the write.
 */
export function reportStaleCsrf(): void {
  onStaleCsrf?.();
}

/** Collapses the visibilitychange + focus pair firing for one return. */
const PASSIVE_COOLDOWN_MS = 1500;

function ensureChannel(): BroadcastChannel | null {
  if (channel) return channel;
  const Ctor = (globalThis as { BroadcastChannel?: typeof BroadcastChannel }).BroadcastChannel;
  if (!Ctor) return null;
  channel = new Ctor(CHANNEL_NAME);
  return channel;
}

/**
 * Posted by every act that swaps the browser's cookie. One shared channel
 * instance publishes AND listens, so the acting tab never receives its own
 * hint (a second instance in the same tab would).
 */
export function announceIdentityChange(): void {
  try {
    ensureChannel()?.postMessage({ type: "identity-changed" });
  } catch {
    // A closed channel must never break the act that tried to announce it;
    // the other tabs still catch up on focus or on their next refusal.
  }
}

/**
 * Who owns the cookie NOW, asked of the server. `"guest"` is 401 - a real
 * answer, not a failure. `"unreachable"` is a network blip or a server error:
 * never a reason to reload, because it says nothing about identity.
 */
export async function fetchLiveIdentity(): Promise<IdentityFingerprint | "guest" | "unreachable"> {
  const fetchImpl = baseFetch ?? window.fetch.bind(window);
  try {
    const response = await fetchImpl("/api/auth/me", { credentials: "include" });
    if (response.status === 401) return "guest";
    if (!response.ok) return "unreachable";
    return identityFingerprint(await response.json() as AuthMeResponse);
  } catch {
    return "unreachable";
  }
}

/**
 * The one comparison: the MOUNTED identity against the cookie's. On a
 * mismatch the tab reloads and renders whoever really owns the cookie.
 * Single-flight - concurrent triggers share one /me read.
 */
export function recheckIdentity(): Promise<"match" | "changed" | "unknown"> {
  if (inflight) return inflight;
  inflight = (async () => {
    const mounted = getMounted?.() ?? null;
    if (mounted === null) return "unknown";
    const live = await fetchLiveIdentity();
    if (live === "unreachable") return "unknown";
    if (identitiesMatch(mounted, live)) return "match";
    reloadPage();
    return "changed";
  })();
  return inflight.finally(() => {
    inflight = null;
    lastPassiveCheckAt = Date.now();
  });
}

function reloadPage(): void {
  window.location.reload();
}

export type ImpersonationEndOutcome =
  | { kind: "identity-changed" }
  | { kind: "ended"; staffRestored: boolean }
  | { kind: "failed" }
  | { kind: "offline" };

/**
 * The bar's exit, made honest (incident step 3): the mounted clock and mode
 * may be stale, so /me is asked FIRST - a cookie that is no longer this
 * impersonation is FOLLOWED (the caller reloads), never POSTed against. And
 * when the end POST is refused, the outcome is read from /me again instead
 * of being pretended: the incident's tab reported an end that answered 403
 * `forbidden` and walked to the console over it.
 */
export async function performImpersonationEnd(
  mounted: IdentityFingerprint,
  csrfToken: string
): Promise<ImpersonationEndOutcome> {
  const before = await fetchLiveIdentity();
  if (before !== "unreachable" && !identitiesMatch(mounted, before)) {
    return { kind: "identity-changed" };
  }
  let response: Response;
  try {
    // Through window.fetch on purpose: the readonly gate passes session
    // management by name, and the observer below must keep seeing the app's
    // own traffic.
    response = await window.fetch("/api/auth/impersonation/end", {
      method: "POST",
      credentials: "include",
      headers: { "X-Launchpad-CSRF": csrfToken }
    });
  } catch {
    // The request never left the browser; /me would not answer either.
    return { kind: "offline" };
  }
  if (!response.ok) {
    const after = await fetchLiveIdentity();
    if (after !== "unreachable" && !identitiesMatch(mounted, after)) {
      return { kind: "identity-changed" };
    }
    return { kind: "failed" };
  }
  const body = await response.json().catch(() => ({})) as { staffRestored?: boolean };
  announceIdentityChange();
  return { kind: "ended", staffRestored: body.staffRestored === true };
}

function passiveRecheck(): void {
  if (Date.now() - lastPassiveCheckAt < PASSIVE_COOLDOWN_MS) return;
  void recheckIdentity();
}

/**
 * Installed ONCE by App. The teardown exists for tests; the product keeps the
 * watch for the life of the page.
 */
export function startIdentityWatch(mounted: () => MountedIdentity): () => void {
  getMounted = mounted;

  const listeningChannel = ensureChannel();
  const onMessage = () => void recheckIdentity();
  listeningChannel?.addEventListener("message", onMessage);

  const onVisible = () => {
    if (!document.hidden) passiveRecheck();
  };
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", passiveRecheck);

  // Trigger 3: the observer wraps fetch BENEATH the readonly gate (the gate
  // installs later and re-wraps), so a refusal the gate synthesizes locally
  // never reaches it - only answers the server really gave. /api/auth paths
  // are exempt: /me is the probe itself, and the session routes answer their
  // own callers (the bar reads the end POST's outcome from /me explicitly).
  const original = window.fetch.bind(window);
  baseFetch = original;
  const observer = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await original(input, init);
    if (response.status !== 401 && response.status !== 403) return response;
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    let path: string;
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch {
      return response;
    }
    if (!path.startsWith("/api/") || path.startsWith("/api/auth/")) return response;
    // BEFORE the surface can print a sentence about this refusal: is it even
    // the mounted identity's refusal? A changed identity reloads instead, and
    // the promise never settles - the page is already on its way down, and
    // resolving would paint the generic error for the reload to erase.
    const verdict = await recheckIdentity();
    if (verdict === "changed") return new Promise<Response>(() => {});
    // The identity is the mounted one, so this refusal is real. One of its
    // reasons is repairable without the reader doing anything: a token that
    // belongs to a session the server has since renewed.
    if (response.status === 403 && onStaleCsrf && (await readErrorCode(response)) === "csrf_invalid") {
      onStaleCsrf();
    }
    return response;
  };
  window.fetch = observer;

  return () => {
    listeningChannel?.removeEventListener("message", onMessage);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("focus", passiveRecheck);
    // Restore only when nothing wrapped above us in the meantime.
    if (window.fetch === observer) window.fetch = original;
    baseFetch = null;
    getMounted = null;
  };
}

/**
 * The server's own error code, read WITHOUT consuming the body the surface is
 * about to read. A response that carries no JSON is simply not this case.
 */
async function readErrorCode(response: Response): Promise<string | null> {
  try {
    const body = (await response.clone().json()) as { error?: unknown };
    return typeof body.error === "string" ? body.error : null;
  } catch {
    return null;
  }
}

/** Test teardown: closes the channel and clears module state. */
export function resetIdentitySync(): void {
  try {
    channel?.close();
  } catch {
    // Already closed.
  }
  channel = null;
  baseFetch = null;
  getMounted = null;
  inflight = null;
  lastPassiveCheckAt = 0;
}
